import { useEffect } from "react";
import { allowedFileTypes } from "./media";
import { isVerifiedValue } from "../utils/authSession";

const BASE_URL = process.env.REACT_APP_NOT_SECRET_CODE;
const GEONAMES_USERNAME = "popup_push";

// (Removed: a dead `englishTimeZone` built from `timeZoneName: 'long'`. It was
// computed at module load, never exported and never used — but it was the last
// copy of the display-name-as-timezone pattern that caused the platform
// timezone bug, so it's gone rather than left as a trap. If you need the
// viewer's zone, use `Intl.DateTimeFormat().resolvedOptions().timeZone`.)

function getProfile() {
  return {
    name: localStorage.getItem("name"),
    email: localStorage.getItem("email"),
    image: localStorage.getItem("image"),
    phone: localStorage.getItem("phone"),
    // access_token is stored in an httpOnly cookie — not accessible from JS
  };
}

function setProfile(data) {
  localStorage.setItem("name", data?.name);
  localStorage.setItem("email", data?.email);
  localStorage.setItem("image", data?.image);
  localStorage.setItem("phone", data?.phone);
  localStorage.setItem("company_id", data?.company_id);
  // access_token intentionally NOT stored in localStorage — it lives in httpOnly cookie
  // Route guards treat any truthy value as "signed in", so an absent/null
  // timestamp must clear the key — never store the string "null"/"undefined".
  if (isVerifiedValue(data?.email_verified_at)) {
    localStorage.setItem("email_verified_at", data.email_verified_at);
  } else {
    localStorage.removeItem("email_verified_at");
  }
  localStorage.setItem("verification_attempts", data?.verification_attempts ?? 0);
}

const emptyLocalStorage = () => {
  localStorage.removeItem("name");
  localStorage.removeItem("email");
  localStorage.removeItem("image");
  localStorage.removeItem("phone");
  localStorage.removeItem("company_id");
  // access_token is in httpOnly cookie — cleared server-side via logout endpoint
  localStorage.removeItem("email_verified_at");
  localStorage.removeItem("verification_attempts");
};

function dateFormate(val) {
  let objectDate = new Date(val);

  let day = objectDate.getDate();
  let month = objectDate.getMonth() + 1;
  let year = objectDate.getFullYear();

  return `${year}-${month}-${day}`;
}

// Runs in an effect: it used to write document.title during render, which is a
// side effect React may run (and discard) any number of times.
function useTitle(title) {
  useEffect(() => {
    document.title = "POPUP PUSH - " + title;
  }, [title]);
}

function setSubTitle(data) {
  localStorage.setItem("SubTile", data?.name);
  localStorage.setItem("SubTile_created", data?.created_at);
}

function getSubTitle() {
  return {
    SubTile: localStorage.getItem("SubTile"),
    SubTile_created: localStorage.getItem("SubTile_created"),
  };
}

function getTitle() {
  const prefix = "POPUP PUSH - ";
  let title = document.title;

  return title.startsWith(prefix) ? title.slice(prefix.length) : title;
}

//To format media size+
const units = ["bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
function formatSize(x) {
  let l = 0,
    n = parseInt(x, 10) || 0;
  while (n >= 1024 && ++l) {
    n = n / 1024;
  }
  return n.toFixed(n < 10 && l > 0 ? 1 : 0) + " " + units[l];
}

//To Format Total duration HH:MM:SS
function formatDuration(totalDuration) {
  // Calculate hours, minutes, seconds, and milliseconds
  var hours = Math.floor(totalDuration / 3600);
  var minutes = Math.floor((totalDuration % 3600) / 60);
  var seconds = Math.floor(totalDuration % 60);

  // Format hours, minutes, seconds, and milliseconds
  if (hours < 10) {
    hours = "0" + hours;
  }

  if (minutes < 10) {
    minutes = "0" + minutes;
  }

  if (seconds < 10) {
    seconds = "0" + seconds;
  }

  return hours + ":" + minutes + ":" + seconds;
}

// Compact duration format — matches the prototypes' own `fmtSecs`/`fmtTotal`
// (.claude/pp-campaign-builder-prototype.html:632, .claude/pp-playlist-page-
// prototype.html:563-564 — identical logic, confirmed: both reduce to plain
// "m:ss" whenever there's no hours component; fmtTotal is just the superset
// that also handles >=1h). Hours are included only when >=1h and the leading
// component (hour or minute) is never zero-padded; the component(s) after it
// always are. Unlike `formatDuration` above (always zero-padded "HH:MM:SS",
// used elsewhere and intentionally left unchanged), this is for the
// campaign-builder/playlist-page row and total-duration chips that want the
// prototype's compact look.
function formatDurationCompact(totalSeconds) {
  const total = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  const mm = hours ? String(minutes).padStart(2, "0") : String(minutes);
  const ss = String(seconds).padStart(2, "0");
  return (hours ? `${hours}:` : "") + `${mm}:${ss}`;
}

// Used in drag and drop components
const onDragEnd = (result, columns, setColumns) => {
  if (!result.destination) return;
  const { source, destination } = result;
  if (source.droppableId !== destination.droppableId) {
    const sourceColumn = columns[source.droppableId];
    const destColumn = columns[destination.droppableId];
    const sourceItems = [...sourceColumn.items];
    const destItems = [...destColumn.items];
    const [removed] = sourceItems.splice(source.index, 1);
    destItems.splice(destination.index, 0, removed);
    setColumns({
      ...columns,
      [source.droppableId]: { ...sourceColumn, items: sourceItems },
      [destination.droppableId]: { ...destColumn, items: destItems },
    });
  } else {
    const column = columns[source.droppableId];
    const copiedItems = [...column.items];
    const [removed] = copiedItems.splice(source.index, 1);
    copiedItems.splice(destination.index, 0, removed);
    setColumns({
      ...columns,
      [source.droppableId]: { ...column, items: copiedItems },
    });
  }
};

//get forms validation errors
const getFormErrorMessage = (name, errors) => {
  return (
    errors[name] && <small className="p-error">{errors[name].message}</small>
  );
};

const prayersNames = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];

function explodeArray(arr) {
  if (!Array.isArray(arr)) {
    return arr;
  }

  if (arr.length === 0) {
    return "";
  }

  const explodedString = arr.slice(0, -1).join(", ");
  return explodedString + (arr.length > 1 ? `, ${arr[arr.length - 1]}` : "");
}

const TOOLTIP_OPTIONS = { showDelay: 100, position: "bottom" };

const validateNotEqual = (value) => {
  return value.toLowerCase() !== "default" || `"default" is a reserved name!`;
};

function trimFileNameExc(name) {
  return name?.split(".")[0];
}

function handleRssSetting(setting, title = "") {
  return {
    ...setting,
    name: title?.split(".")[0],
    title: title?.split(".")[0],
    text_color: setting?.text_color ?? setting?.textColor,
    direction: setting?.direction === "ltr" ? "left" : "right",
    font_size: Number(setting?.font_size),
    bg_color: setting?.bg_color ?? setting?.bgColor,
  };
}

const generateString = (length, fileType) => {
  const ext =
    fileType.toLowerCase() === "mpeg" ? "mp3" : fileType.toLowerCase();
  if (!allowedFileTypes.includes(ext)) {
    return null; // Skip invalid file types
  }
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const randomString = Array.from({ length }, () =>
    chars.charAt(Math.floor(Math.random() * chars.length))
  ).join("");
  return `${randomString}.${ext}`; // Add the extension
};


/**
 * Render a backend timestamp in the VIEWER's local timezone.
 *
 * The API now emits UTC ISO-8601 with an explicit offset
 * ("2026-07-28T17:00:00+00:00"), which `new Date()` parses unambiguously.
 *
 * This used to `.split(" ")` the old naive 'Y-m-d H:i' wire format and then
 * force the result into a hardcoded "Asia/Amman" — so it (a) blew up on the
 * new format, producing "...+00:00Tundefined" -> Invalid Date -> a raw ISO
 * string leaking into the UI, and (b) showed Jordan's clock to every venue on
 * earth. `timeZone` is left overridable but now defaults to undefined, which
 * makes Intl use the viewer's own zone.
 *
 * The legacy naive format is still accepted so a stale cached response or an
 * un-migrated endpoint degrades gracefully rather than rendering garbage.
 */
function formatPublishedDate(dateString, timeZone = undefined) {
  if (!dateString) return "";

  try {
    // Legacy 'Y-m-d H:i' (no offset) -> treat as UTC, which is how it was
    // always stored. Anything else (ISO) is already unambiguous.
    const raw = String(dateString);
    const date = raw.includes("T")
      ? new Date(raw)
      : new Date(`${raw.replace(" ", "T")}Z`);

    if (isNaN(date.getTime())) return raw;

    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      ...(timeZone ? { timeZone } : {}),
    }).format(date);
  } catch (error) {
    console.error("Invalid date format:", dateString);
    return dateString;
  }
}


export {
  formatPublishedDate,
  TOOLTIP_OPTIONS,
  GEONAMES_USERNAME,
  prayersNames,
  BASE_URL,
  trimFileNameExc,
  getProfile,
  setProfile,
  useTitle,
  getTitle,
  setSubTitle,
  getSubTitle,
  dateFormate,
  formatSize,
  formatDuration,
  formatDurationCompact,
  onDragEnd,
  getFormErrorMessage,
  emptyLocalStorage,
  explodeArray,
  validateNotEqual,
  handleRssSetting,
  generateString,
};
